import { create } from 'zustand';
import type { MatterFullState, Stage } from '@/types';

interface AppStore {
  matterId: string | null;
  matterState: MatterFullState | null;
  activeStage: Stage;
  readOnlyStage: Stage | null;    // When set, Stage N is shown read-only for review
  scrollToClaimId: string | null;  // Target claim id to scroll to after mount
  /** Cross-stage banner shown after a hard reset. Stage 3 sets it
   *  immediately before navigating to Stage 1; Stage 1 reads + renders
   *  it (the banner state has to live above both stages because
   *  Stage 3 unmounts the moment activeStage flips). */
  resetBanner: { count: number } | null;
  loading: boolean;
  error: string | null;
  setMatterId: (id: string | null) => void;
  setMatterState: (s: MatterFullState) => void;
  setActiveStage: (s: Stage) => void;
  setReadOnlyStage: (s: Stage | null) => void;
  setScrollToClaimId: (id: string | null) => void;
  setResetBanner: (b: { count: number } | null) => void;
  setLoading: (l: boolean) => void;
  setError: (e: string | null) => void;
  reset: () => void;
}

export const useAppStore = create<AppStore>((set) => ({
  matterId: null,
  matterState: null,
  activeStage: 'stage_1',
  readOnlyStage: null,
  scrollToClaimId: null,
  resetBanner: null,
  loading: false,
  error: null,
  setMatterId: (id) => set({ matterId: id }),
  // setMatterState does NOT touch activeStage any more. Previously it
  // snapped activeStage to matter.current_stage on every update, which
  // was fine for the initial matter-open flow but actively broke
  // in-session navigation: Stage 3 actions (broaden / narrow / edit /
  // etc.) call refreshMatter → setMatterState, which in turn flipped
  // activeStage back to whatever current_stage still said (typically
  // 'stage_2_5' until Stage 3 itself freezes), yanking the drafter
  // back to Stage 2. Callers that want to land on the matter's
  // current_stage (Home.tsx on open, MattersPage on open) now call
  // setActiveStage explicitly alongside setMatterState.
  setMatterState: (s) => set({ matterState: s }),
  // Note: callers decide whether to clear readOnlyStage. The TopBar clears it
  // on normal stage navigation; Stage 4's "Jump to claim" sets readOnlyStage
  // first and then navigates, so we must not auto-clear here.
  setActiveStage: (s) => set({ activeStage: s }),
  setReadOnlyStage: (s) => set({ readOnlyStage: s }),
  setScrollToClaimId: (id) => set({ scrollToClaimId: id }),
  setResetBanner: (b) => set({ resetBanner: b }),
  setLoading: (l) => set({ loading: l }),
  setError: (e) => set({ error: e }),
  reset: () => set({
    matterId: null, matterState: null, activeStage: 'stage_1',
    readOnlyStage: null, scrollToClaimId: null, resetBanner: null,
    loading: false, error: null,
  }),
}));
